import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { standardComponentTypeGroups } from '../../../Helpers/componentTypeOptions';

interface ComponentTypeSelectProps {
    /** A standard `config/buildComponentTypes` key, or null for none */
    value: string | null;
    onChange: (value: string | null) => void;
    disabled?: boolean;
    id?: string;
    /** Text of the empty option */
    noneLabel?: string;
}

/**
 * Picks a standard component type (no per-instance `_custom_` types), grouped by base
 * type: a Kontrollmall's suggested `componentSpecificType`.
 */
const ComponentTypeSelect: React.FC<ComponentTypeSelectProps> = ({ value, onChange, disabled, id, noneLabel = 'Ingen komponenttyp' }) => {
    const { types, getTranslation } = useBuildComponentTypes();
    const groups = useMemo(() => standardComponentTypeGroups(types), [types]);
    // A value no longer offered (a removed type) stays selectable so it is not dropped silently
    const known = !value || groups.some(group => group.types.some(type => type.key === value));

    return (
        <Form.Select
            id={id}
            value={value ?? ''}
            onChange={e => onChange(e.target.value || null)}
            disabled={disabled}
            aria-label="Komponenttyp"
        >
            <option value="">{noneLabel}</option>
            {!known && value && <option value={value}>{getTranslation(value)}</option>}
            {groups.map(group => (
                <optgroup key={group.baseType} label={group.label}>
                    {group.types.map(type => (
                        <option key={type.key} value={type.key}>{type.name}</option>
                    ))}
                </optgroup>
            ))}
        </Form.Select>
    );
};

export default ComponentTypeSelect;
